import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export type MeetingType = 'STEERING_COMMITTEE' | 'WORKING_SESSION' | 'VENDOR_DEMONSTRATION';
export type MeetingStatus = 'SCHEDULED' | 'HELD' | 'CANCELLED';

export const MEETING_TYPE: Record<MeetingType, string> = {
  STEERING_COMMITTEE: 'Steering Committee',
  WORKING_SESSION: 'Working session',
  VENDOR_DEMONSTRATION: 'Vendor demonstration',
};
export const MEETING_STATUS: Record<MeetingStatus, { label: string; tone: 'info' | 'ok' | 'neutral' }> = {
  SCHEDULED: { label: 'Scheduled', tone: 'info' },
  HELD: { label: 'Held', tone: 'ok' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export interface MeetingRow {
  id: string;
  type: MeetingType;
  title: string;
  heldAt: string;
  status: MeetingStatus;
  attendees: number;
  actionItems: number;
  decisions: number;
  hasMinutes: boolean;
}

export interface Attendee {
  name: string;
  title?: string;
  userId?: string;
}

export interface MeetingDetail {
  id: string;
  type: MeetingType;
  title: string;
  heldAt: string;
  status: MeetingStatus;
  attendees: Attendee[];
  agenda: string | null;
  minutes: { id: string; title: string; type: string } | null;
  documents: Array<{ id: string; title: string }>;
  actionItems: Array<{
    id: string;
    title: string;
    status: string;
    owner: string;
    dueDate: string | null;
    phase: number;
  }>;
  decisions: Array<{
    id: string;
    ref: string;
    title: string;
    status: string;
    choice: string | null;
    due: string | null;
  }>;
  canManage: boolean;
}

export interface Calendar {
  month: string;
  today: string;
  meetings: Array<{ id: string; type: MeetingType; title: string; status: MeetingStatus; date: string }>;
  expected: Array<{ date: string; cadenceId: string; title: string; type: MeetingType; met: boolean; missed: boolean }>;
}

export interface Cadence {
  id?: string;
  type: MeetingType;
  title: string;
  frequency: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY';
  rule: string;
  description?: string;
}

export const meetingsKey = (programId: string) => ['meetings', programId] as const;

export const useMeetings = (programId: string) =>
  useQuery({
    queryKey: [...meetingsKey(programId), 'list'],
    queryFn: () => api.get<MeetingRow[]>(`/api/programs/${programId}/meetings`),
  });

export const useMeeting = (programId: string, id: string) =>
  useQuery({
    queryKey: [...meetingsKey(programId), 'one', id],
    queryFn: () => api.get<MeetingDetail>(`/api/programs/${programId}/meetings/${id}`),
  });

export const useCalendar = (programId: string, month: string) =>
  useQuery({
    queryKey: [...meetingsKey(programId), 'calendar', month],
    queryFn: () => api.get<Calendar>(`/api/programs/${programId}/meetings-calendar/${month}`),
  });

export const useCadences = (programId: string) =>
  useQuery({
    queryKey: [...meetingsKey(programId), 'cadences'],
    queryFn: () => api.get<Cadence[]>(`/api/programs/${programId}/meeting-cadences`),
  });

/** "2026-11" shifted by n months. */
export function shiftMonth(m: string, n: number) {
  const d = new Date(`${m}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 7);
}

/** A local date and time input value ("2026-11-05T10:00") to an ISO instant in the browser's zone. */
export const localInputToIso = (v: string) => new Date(v).toISOString();
export const isoToLocalInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
